/**
 * CONFIRM PAYMENT DIALOG
 * Dialog modal de confirmation manuelle virement/chèque
 * - Sélection du payeur (contact du dossier) obligatoire — TICKET-PAY-01
 * - Commentaire obligatoire tracé en timeline
 * - Email de confirmation au(x) participant(s) après confirmation d'un
 *   virement/chèque (TICKET-MAIL-06, symétrique du mail CB envoyé par
 *   l'IPN) — via Edge Function send-offline-confirmation. Non bloquant :
 *   un échec d'envoi n'annule pas la confirmation. La régularisation CB
 *   ancienne plateforme (PAY-12) n'envoie pas de mail.
 * - BED (TICKET-BED-25) : montant réellement reçu, saisi par le staff (pas de
 *   pré-remplissage). En dessous du restant dû, versement partiel : le
 *   dossier passe en `partial`, le lien de paiement reste actif et le CRM
 *   n'envoie pas de mail (le mail « solde réglé » part au versement qui
 *   solde). Un dossier `partial` est confirmable en précisant le moyen reçu.
 *   Rien de dû (dossier déjà couvert) : la confirmation clôt sans encaissement
 *   ni mail.
 * - BED (00295) : un montant SUPÉRIEUR au restant dû est enregistré tel quel
 *   (transparence demandée par la compta) : le dossier est soldé, le mail
 *   part, et le trop-perçu s'affiche sur la fiche jusqu'à son remboursement
 *   (fait à la main, puis noté depuis la fiche). Un encaissement ne se
 *   corrige pas depuis le CRM : au-delà du restant dû, le staff doit cocher
 *   une confirmation explicite (faute de frappe « 13000 » pour « 1300 »).
 */

import { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select } from '../ui/select';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from '../ui';
import { useSupabase } from '../../hooks/useSupabase';
import { usePaymentActions } from '../../hooks/usePaymentActions';
import { usePaymentCaseContacts } from '../../hooks/usePaymentCaseContacts';
import type { PayerCandidate } from '../../hooks/usePaymentCaseContacts';
import type { PaymentCaseWithRelations } from '../../hooks/usePaymentCase';
import { parseReceivedAmount } from '../../lib/payment/received-amount';
import {
  PAYMENT_METHOD,
  PAYMENT_SCOPE,
  PAYMENT_SCOPE_LABELS,
  PAYMENT_STATUS,
} from '../../types/payment';

interface ConfirmPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paymentCase: PaymentCaseWithRelations;
  prospectName: string;
  productName: string;
  onConfirmed: () => void;
}

function formatCandidateLabel(candidate: PayerCandidate): string {
  const fullName = `${candidate.first_name ?? ''} ${candidate.last_name ?? ''}`.trim() || 'Contact';
  if (candidate.role === 'pilot') return `${fullName} (pilote)`;
  if (candidate.role === 'copilot') return `${fullName} (copilote)`;
  return fullName;
}

// Langues supportées par les templates email côté 4LT (Resend + next-intl) —
// même liste que ResendBpiLinkDialog / BedLinkDialog.
const EMAIL_LOCALES = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
] as const;

type EmailLocale = (typeof EMAIL_LOCALES)[number]['value'];

type OfflineMethod = typeof PAYMENT_METHOD.TRANSFER | typeof PAYMENT_METHOD.CHEQUE;

const OFFLINE_METHOD_OPTIONS = [
  { value: PAYMENT_METHOD.TRANSFER, label: 'Virement' },
  { value: PAYMENT_METHOD.CHEQUE, label: 'Chèque' },
];

function formatEuros(cents: number, currency: string): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(cents / 100);
}

export function ConfirmPaymentDialog({
  open,
  onOpenChange,
  paymentCase,
  prospectName,
  productName,
  onConfirmed,
}: ConfirmPaymentDialogProps) {
  const [comment, setComment] = useState('');
  const [payerContactId, setPayerContactId] = useState<string>('');
  const [emailLocale, setEmailLocale] = useState<EmailLocale>('fr');
  const [amountInput, setAmountInput] = useState('');
  const [overpaymentConfirmed, setOverpaymentConfirmed] = useState(false);
  const [partialMethod, setPartialMethod] = useState<OfflineMethod>(
    paymentCase.method === PAYMENT_METHOD.CHEQUE ? PAYMENT_METHOD.CHEQUE : PAYMENT_METHOD.TRANSFER
  );
  const [submitting, setSubmitting] = useState(false);
  const supabase = useSupabase();
  const { confirmPayment } = usePaymentActions();

  const {
    candidates,
    loading: loadingCandidates,
    error: candidatesError,
  } = usePaymentCaseContacts({
    paymentCaseId: open ? paymentCase.id : undefined,
  });

  // Pré-sélection automatique :
  // - dossier solo (1 seul contact) → sélection immédiate, pas de blocage
  // - dossier avec payer_contact_id déjà défini (ex. cas IPN antérieur) → le sélectionner
  // - sinon → laisser l'utilisateur choisir
  useEffect(() => {
    if (!open) return;
    if (candidates.length === 0) return;

    if (payerContactId && candidates.some((c) => c.contact_id === payerContactId)) {
      return;
    }

    if (candidates.length === 1) {
      setPayerContactId(candidates[0].contact_id);
      return;
    }

    if (
      paymentCase.payer_contact_id &&
      candidates.some((c) => c.contact_id === paymentCase.payer_contact_id)
    ) {
      setPayerContactId(paymentCase.payer_contact_id);
    }
  }, [open, candidates, payerContactId, paymentCase.payer_contact_id]);

  const isManualCardConfirmation =
    paymentCase.status === PAYMENT_STATUS.PENDING_CARD &&
    paymentCase.method === PAYMENT_METHOD.CARD;

  // BED-25 : un dossier BED `partial` (versement déjà reçu) se confirme en
  // précisant le moyen reçu ; un pending_* garde le moyen de son statut.
  const isPartialCase = paymentCase.status === PAYMENT_STATUS.PARTIAL;
  const offlineMethod: OfflineMethod = isPartialCase
    ? partialMethod
    : paymentCase.status === PAYMENT_STATUS.PENDING_CHEQUE
      ? PAYMENT_METHOD.CHEQUE
      : PAYMENT_METHOD.TRANSFER;

  const methodLabel = isManualCardConfirmation
    ? 'CB ancienne plateforme'
    : (isPartialCase ? partialMethod : paymentCase.method) === PAYMENT_METHOD.TRANSFER
      ? 'virement'
      : 'chèque';

  // BED : le staff saisit le montant réellement reçu, comparé au RESTANT DÛ
  // (des fractions CB ou des versements ont pu être encaissés avant) : en
  // dessous, versement partiel (00294) ; au-delà, trop-perçu (00295).
  // BPI : montant total du dossier, non modifiable.
  const isBed = paymentCase.scope === PAYMENT_SCOPE.BED;
  const isBedOffline = isBed && !isManualCardConfirmation;
  const succeededCents = isBed
    ? paymentCase.payment_attempts.reduce((acc, a) => (a.succeeded ? acc + a.amount_cents : acc), 0)
    : 0;
  const dueCents = isBed
    ? Math.max(0, paymentCase.amount_cents - succeededCents)
    : paymentCase.amount_cents;

  const amountFormatted = formatEuros(dueCents, paymentCase.currency);

  // À chaque ouverture : montant vide (le staff recopie le montant du relevé ou
  // du chèque ; pré-remplir le restant dû ferait solder d'un clic un virement
  // partiel, l'incident C-21), moyen = dernier moyen du dossier.
  useEffect(() => {
    if (!open || !isBedOffline) return;
    setAmountInput('');
    setOverpaymentConfirmed(false);
    setPartialMethod(
      paymentCase.method === PAYMENT_METHOD.CHEQUE ? PAYMENT_METHOD.CHEQUE : PAYMENT_METHOD.TRANSFER
    );
  }, [open, isBedOffline, paymentCase.method]);

  // Restant dû nul (remise posée après un versement) : rien à saisir, la
  // confirmation clôt le dossier sans encaissement (00140).
  const needsAmount = isBedOffline && dueCents > 0;
  const amountCents = needsAmount ? parseReceivedAmount(amountInput) : null;
  const amountValid = amountCents !== null && amountCents > 0;
  const isPartialAmount =
    needsAmount && amountCents !== null && amountValid && amountCents < dueCents;
  const overpaidCents =
    needsAmount && amountCents !== null && amountValid ? Math.max(0, amountCents - dueCents) : 0;
  // BED déjà couvert : la confirmation clôt sans rien encaisser, aucun mail
  // « virement reçu » (la route 4LT annoncerait un montant jamais reçu).
  const isNothingDue = isBedOffline && !needsAmount;

  const payerOptions = useMemo(
    () =>
      candidates.map((candidate) => ({
        value: candidate.contact_id,
        label: formatCandidateLabel(candidate),
      })),
    [candidates]
  );

  // Verrouillage UI : le payeur n'est fige qu'apres un statut terminal
  // (paid, expired, failed). Avant, le staff peut corriger le payeur meme
  // s'il a deja ete identifie par le tunnel public. Aligne avec la
  // migration 00076 (payer_contact_id mutable until terminal).
  const TERMINAL_STATUSES = ['paid', 'expired', 'failed'] as const;
  const payerLocked =
    !!paymentCase.payer_contact_id &&
    candidates.some((c) => c.contact_id === paymentCase.payer_contact_id) &&
    TERMINAL_STATUSES.includes(paymentCase.status as (typeof TERMINAL_STATUSES)[number]);

  const canSubmit =
    !!comment.trim() &&
    !!payerContactId &&
    !submitting &&
    !loadingCandidates &&
    (!needsAmount || amountValid) &&
    (overpaidCents === 0 || overpaymentConfirmed);

  async function handleConfirm() {
    if (!comment.trim() || !payerContactId) return;

    setSubmitting(true);
    const result = await confirmPayment({
      paymentCaseId: paymentCase.id,
      comment: comment.trim(),
      contactId: payerContactId,
      payerContactId,
      // Le moyen est aussi envoyé pour un pending_* : le serveur refuse s'il
      // ne correspond plus au statut (écran périmé). Idem pour le restant dû
      // affiché (00295) : une double saisie du même virement est refusée.
      ...(isBedOffline ? { method: offlineMethod, expectedRemainingCents: dueCents } : {}),
      ...(needsAmount && amountCents !== null ? { amountCents } : {}),
    });

    if (result.success) {
      if (isManualCardConfirmation) {
        toast.success('Paiement CB confirmé manuellement');
      } else if (result.status === PAYMENT_STATUS.PARTIAL) {
        // Versement partiel : pas de mail « solde réglé » (même règle que
        // l'IPN pour une fraction CB), le lien de paiement reste actif.
        toast.success(
          `Versement de ${formatEuros(amountCents ?? 0, paymentCase.currency)} enregistré — reste ${formatEuros(result.remainingCents ?? 0, paymentCase.currency)} à régler.`
        );
      } else if (isNothingDue) {
        toast.success('Dossier clos : rien à encaisser, aucun email envoyé.');
      } else {
        // 00295 : trop-perçu rappelé au staff (il reste visible sur la fiche).
        const overpaidNote =
          result.overpaidCents && result.overpaidCents > 0
            ? ` Trop-perçu de ${formatEuros(result.overpaidCents, paymentCase.currency)} à rembourser.`
            : '';
        // Email de confirmation au(x) participant(s) — résolution serveur des
        // destinataires (Edge Function -> route interne 4LT, même règle que le
        // mail CB de l'IPN). Échec non bloquant : le paiement reste confirmé.
        try {
          const { error: invokeError } = await supabase.functions.invoke(
            'send-offline-confirmation',
            {
              body: { payment_case_id: paymentCase.id, locale: emailLocale },
            }
          );
          if (invokeError) throw invokeError;
          toast.success(`Paiement confirmé — email de confirmation envoyé.${overpaidNote}`);
        } catch (err) {
          console.error('[ConfirmPaymentDialog] send-offline-confirmation error:', err);
          toast.error(
            `Paiement confirmé, mais l'envoi de l'email de confirmation a échoué. Vous pouvez prévenir le participant manuellement.${overpaidNote}`
          );
        }
      }
      setComment('');
      setPayerContactId('');
      setEmailLocale('fr');
      setAmountInput('');
      setOverpaymentConfirmed(false);
      onOpenChange(false);
      onConfirmed();
    } else {
      // Remonte le message specifique si fourni (ex. conflit de payeur),
      // sinon message generique.
      toast.error(result.error ?? 'Erreur lors de la confirmation du paiement');
    }
    setSubmitting(false);
  }

  function handleClose(value: boolean) {
    if (!submitting) {
      setComment('');
      setPayerContactId('');
      setEmailLocale('fr');
      setAmountInput('');
      setOverpaymentConfirmed(false);
      onOpenChange(value);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>
            {isManualCardConfirmation
              ? 'Confirmer paiement CB ancienne plateforme'
              : isNothingDue
                ? 'Clore le dossier (rien à encaisser)'
                : `Confirmer la réception du ${methodLabel}`}
          </DialogTitle>
          <DialogDescription>
            Prospect <strong>{prospectName}</strong> — {PAYMENT_SCOPE_LABELS[paymentCase.scope]}{' '}
            {productName} — <strong>{amountFormatted}</strong>
            {isBed ? ' (restant dû)' : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {isManualCardConfirmation && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Cette action confirme le paiement dans le CRM pour un encaissement CB réalisé hors
              module actuel. Elle ne crée pas de transaction Sogecommerce.
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-zinc-700">
              Payeur <span className="text-red-500">*</span>
            </label>
            {loadingCandidates ? (
              <p className="text-xs text-zinc-400">Chargement des contacts...</p>
            ) : candidatesError ? (
              <p className="text-xs text-red-500">{candidatesError}</p>
            ) : candidates.length === 0 ? (
              <p className="text-xs text-red-500">Aucun contact disponible pour ce dossier.</p>
            ) : (
              <Select
                value={payerContactId}
                onChange={(e) => setPayerContactId(e.target.value)}
                disabled={submitting || candidates.length === 1 || payerLocked}
                placeholder={candidates.length > 1 ? 'Sélectionner le payeur' : undefined}
                options={payerOptions}
                required
              />
            )}
            {payerLocked ? (
              <p className="text-xs text-zinc-500">
                Le payeur a été identifié lors du paiement en ligne et ne peut plus être modifié.
              </p>
            ) : (
              <p className="text-xs text-zinc-400">
                Le payeur sera tracé sur la transaction pour le suivi comptable.
              </p>
            )}
          </div>

          {isBedOffline && isPartialCase && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-zinc-700" htmlFor="confirm-offline-method">
                Moyen de paiement reçu <span className="text-red-500">*</span>
              </label>
              <Select
                id="confirm-offline-method"
                value={partialMethod}
                onChange={(e) => setPartialMethod(e.target.value as OfflineMethod)}
                disabled={submitting}
                options={OFFLINE_METHOD_OPTIONS}
              />
            </div>
          )}

          {needsAmount && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-zinc-700" htmlFor="confirm-amount">
                Montant reçu (€) <span className="text-red-500">*</span>
              </label>
              {/* type="text" : un champ number avale la virgule dans un
                  navigateur en anglais (« 50,50 » lu 5050). */}
              <Input
                id="confirm-amount"
                type="text"
                inputMode="decimal"
                value={amountInput}
                onChange={(e) => {
                  setAmountInput(e.target.value);
                  // La confirmation vaut pour UN montant : toute retouche l'annule.
                  setOverpaymentConfirmed(false);
                }}
                placeholder={`Restant dû : ${amountFormatted}`}
                disabled={submitting}
              />
              {amountInput.trim() === '' ? (
                <p className="text-xs text-zinc-400">
                  Montant figurant sur le relevé bancaire ou sur le chèque.
                </p>
              ) : amountCents === null ? (
                <p className="text-xs text-red-500">
                  Montant illisible : chiffres avec au plus 2 décimales (ex. 1 200,50).
                </p>
              ) : !amountValid ? (
                <p className="text-xs text-red-500">Saisir un montant supérieur à 0.</p>
              ) : overpaidCents > 0 ? (
                <p className="text-xs text-amber-700">
                  {formatEuros(amountCents ?? 0, paymentCase.currency)} pour{' '}
                  {formatEuros(dueCents, paymentCase.currency)} restant dû : trop-perçu de{' '}
                  {formatEuros(overpaidCents, paymentCase.currency)}. Le dossier sera soldé et un
                  email de confirmation envoyé ; le trop-perçu restera affiché sur la fiche
                  jusqu&apos;à ce que son remboursement soit noté.
                </p>
              ) : isPartialAmount ? (
                <p className="text-xs text-amber-700">
                  Versement partiel de {formatEuros(amountCents ?? 0, paymentCase.currency)} : il
                  restera {formatEuros(dueCents - (amountCents ?? 0), paymentCase.currency)} à
                  régler. Le lien de paiement reste actif ; l&apos;email de confirmation partira
                  avec le versement qui solde le dossier.
                </p>
              ) : (
                <p className="text-xs text-zinc-400">
                  {formatEuros(amountCents ?? 0, paymentCase.currency)} : ce montant solde le
                  dossier, un email de confirmation sera envoyé.
                </p>
              )}
            </div>
          )}

          {overpaidCents > 0 && (
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="confirm-overpayment"
                checked={overpaymentConfirmed}
                disabled={submitting}
                onChange={(e) => setOverpaymentConfirmed(e.target.checked)}
                className="mt-0.5 size-4 shrink-0 rounded border-zinc-300 text-amber-600 focus:ring-amber-500"
              />
              <label htmlFor="confirm-overpayment" className="cursor-pointer text-sm text-zinc-700">
                Je confirme avoir reçu {formatEuros(amountCents ?? 0, paymentCase.currency)}, soit{' '}
                {formatEuros(overpaidCents, paymentCase.currency)} de plus que le restant dû.
                L&apos;encaissement ne pourra pas être corrigé depuis le CRM.
              </label>
            </div>
          )}

          {isNothingDue && (
            <p className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
              Rien n&apos;est dû sur ce dossier : la confirmation le clôt sans encaissement ni
              email.
            </p>
          )}

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-zinc-700">
              Commentaire <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={
                isManualCardConfirmation
                  ? 'Ex: Paiement CB confirmé sur ancienne plateforme le 28/03.'
                  : isNothingDue
                    ? 'Ex: Solde couvert par les CB après la remise du 28/03.'
                    : `Ex: ${methodLabel === 'virement' ? 'Virement reçu le 28/03, réf. bancaire VIR-...' : 'Chèque reçu le 28/03, n° ...'}`
              }
              rows={4}
              disabled={submitting}
            />
            <p className="text-xs text-zinc-400">
              Ce commentaire sera enregistré dans la timeline du dossier.
            </p>
          </div>

          {!isManualCardConfirmation && !isPartialAmount && !isNothingDue && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-zinc-700">
                Langue de l&apos;email de confirmation
              </label>
              <Select
                value={emailLocale}
                onChange={(e) => setEmailLocale(e.target.value as EmailLocale)}
                disabled={submitting}
                options={EMAIL_LOCALES.map((l) => ({ value: l.value, label: l.label }))}
              />
              <p className="text-xs text-zinc-400">
                Un email de confirmation de paiement sera envoyé au(x) participant(s) du dossier.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={submitting}>
            Annuler
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canSubmit}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {submitting
              ? 'Confirmation...'
              : isPartialAmount
                ? 'Enregistrer le versement partiel'
                : isNothingDue
                  ? 'Clore le dossier'
                  : 'Confirmer le paiement'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
